import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button, cn } from "@guardian/shared-ui";
import { LanguageSwitcher } from "./language-switcher";
import { ThemeSwitcher } from "./theme-switcher";
import { isAllowedNavigation } from "@/utils/urlValidation";

export const MainHeader: React.FC = () => {
  const { t } = useTranslation();
  const homeUrl = import.meta.env.VITE_HOME_URL;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Determine if the header should show a scrolled style
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Array of navigation items
  const navItems = [
    { key: "about", label: t("nav.about"), href: `${homeUrl}/about` },
    { key: "contact", label: t("nav.contact"), href: `${homeUrl}/contact` },
  ];

  const handleNavItemClick = (href: string) => {
    if (isAllowedNavigation(href)) {
      window.location.href = href;
    }
    setIsMenuOpen(false); // close mobile menu on navigation
  };

  return (
    <header
      className={cn(
        "sticky top-0 z-50 w-full border-b backdrop-blur-md transition-all duration-300 safe-top",
        isScrolled ? "bg-background/80" : "bg-background"
      )}
    >
      <div className="container mx-auto px-4">
        {/* Header inner wrapper */}
        <div className="flex h-20 items-center justify-between">
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center gap-2"
          >
            <a href="/" className="flex items-center space-x-2">
              <img
                src="/Logo.svg"
                alt="Guardian Logo"
                className="h-12 w-auto dark:hidden"
              />
              <img
                src="/Logo2.svg"
                alt="Guardian Logo"
                className="h-12 w-auto hidden dark:block"
              />
            </a>
          </motion.div>

          {/* Right side: Language/Theme switchers + Desktop Nav + Mobile Menu Button */}
          <div className="flex items-center gap-4">

            {/* Desktop Navigation (hidden on mobile) */}
            <motion.nav
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="hidden md:flex items-center gap-6"
            >
              {navItems.map((item) => (
                <Button
                  key={item.key}
                  variant="ghost"
                  size="sm"
                  className="font-poppins-medium text-base hover:text-primary"
                  onClick={() => handleNavItemClick(item.href)}
                >
                  {item.label}
                </Button>
              ))}
            </motion.nav>

            {/* Language & Theme switchers */}
            <LanguageSwitcher />
            <ThemeSwitcher />

            {/* Mobile Menu Button (visible only on small screens) */}
            <div className="md:hidden">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsMenuOpen((prev) => !prev)}
                aria-label="Toggle Mobile Menu"
              >
                <Menu className="h-6 w-6" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      <AnimatePresence mode="wait">
        {isMenuOpen && (
          <motion.div
            key="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden border-t bg-background"
          >
            <div className="container px-4 py-4">
              <nav className="flex flex-col space-y-4 mb-6">
                {navItems.map((item) => (
                  <motion.button
                    key={item.key}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.2 }}
                    className="text-left font-poppins-medium text-lg text-muted-foreground hover:text-foreground"
                    onClick={() => handleNavItemClick(item.href)}
                  >
                    {item.label}
                  </motion.button>
                ))}
              </nav>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
